import { Column, ColumnType } from "./types";
import { MRT_ColumnDef } from "mantine-react-table";
import { addCellToColumn, addHeaderToColumn, getBaseColumn } from "./utils";
import { RowData } from "../types";

const getMrtColumnFromColumn = <T extends RowData>(
    column: Column<T>,
    options?: {
        isFirstOfGroup: boolean;
        isLastOfGroup: boolean;
    },
): MRT_ColumnDef<T> => {
    const baseColumn: MRT_ColumnDef<T> = getBaseColumn(column, options);
    const columnWithCell: MRT_ColumnDef<T> = addCellToColumn(baseColumn, column);
    const finalColumn: MRT_ColumnDef<T> = addHeaderToColumn(columnWithCell, column);

    return finalColumn;
};

export const getMRTColumnsFromColumns = <T extends RowData>(
    columns: Array<Column<T>>,
): Array<MRT_ColumnDef<T>> => {
    // Get the unique header groups from the columns
    const uniqueHeaderGroups: Array<string> = [
        ...new Set(
            columns
                .filter(
                    (column): column is Column<T> & { headerGroup: string } =>
                        column.headerGroup !== undefined,
                )
                .map((column) => column.headerGroup),
        ),
    ];

    // Get the last column from each group (this will be used later on)
    const lastColumnsInGroup = new Map();
    columns.forEach((column, i) => {
        const headerGroup = column.headerGroup;
        if (headerGroup) {
            lastColumnsInGroup.set(headerGroup, i);
        }
    });

    // Iterate the columns and create a new array of MRT_ColumnDef
    const mrtColumnsWithGroups: Array<MRT_ColumnDef<T>> = [];
    columns.forEach((column, i) => {
        // Get the header group for this column
        const headerGroup = column.headerGroup;

        // If the column has a header group, find the corresponding MRT_ColumnDef (or create one) and add this column to it
        if (headerGroup !== undefined && uniqueHeaderGroups.includes(headerGroup)) {
            let groupColumn = mrtColumnsWithGroups.find((col) => col.header === headerGroup);

            // If the group column doesn't exist, create it
            if (!groupColumn) {
                groupColumn = getMrtColumnFromColumn({
                    header: headerGroup,
                    accessorKey: null,
                    columnType: ColumnType.GroupHeader,
                });
                mrtColumnsWithGroups.push(groupColumn);
            }

            if (!groupColumn.columns) {
                groupColumn.columns = [];
            }

            // Create the MRT_ColumnDef for this column, considering if it's the first or last column in the group, and add it to the group column
            const isFirstOfGroup = groupColumn.columns.length === 0;
            const isLastOfGroup = lastColumnsInGroup.get(headerGroup) === i;

            const mrtColumn = getMrtColumnFromColumn(column, {
                isFirstOfGroup,
                isLastOfGroup,
            });
            groupColumn.columns.push(mrtColumn);
        } else {
            // If the column doesn't have a header group, just add it to the MRT_ColumnDef array
            const mrtColumn = getMrtColumnFromColumn(column);
            mrtColumnsWithGroups.push(mrtColumn);
        }
    });

    return mrtColumnsWithGroups;
};
